import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { constants } from '../../../constants';
import { UserData } from '../../user-admin/models/user-data.model';
import { Permissions } from '../models/permissions.model';
import { AuthResponse } from '../models/auth-response.model';
import { LoginRequest } from '../models/login-request.model';
import { User, UserRole, userRoles } from '../models/user.model';
import { DashboardStatisticsService } from '../../main/services/dashboard-statistics.service';
import * as Sentry from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  public currentUser: Observable<User>;
  private currentUserSubject: BehaviorSubject<User>;
  private ngUnsubscribe: Subject<void> = new Subject();
  private hj = (window as any).hj;

  private _categoriesEnabled$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public categoriesEnabled$: Observable<boolean> =
    this._categoriesEnabled$.asObservable();
  public _userRoles$: BehaviorSubject<userRoles> =
    new BehaviorSubject<userRoles>(undefined);
  public userRoles$: Observable<userRoles> = this._userRoles$.asObservable();
  private _incomeGroupEnabled$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public incomeGroupEnabled$: Observable<boolean> =
    this._incomeGroupEnabled$.asObservable();
  private _permissions$ = new BehaviorSubject<Permissions>({
    isFreemium: false,
    isGenerationsAvailable: false,
    freemiumCategory: '',
    categories: [],
    allowedCountries: undefined,
    allowedCountryCities: undefined,
    allowedCities: undefined,
    allowedYears: [],
  });
  public permissions$ = this._permissions$.asObservable();
  public openFreemiumRequestModal: Subject<{
    isOpen: boolean;
    requestCategory: string;
  }> = new Subject();
  private freemiumIntroductionModalOpenedSubject = new BehaviorSubject<boolean>(
    false
  );
  public isFreemiumIntroductionModalOpened$: Observable<boolean> =
    this.freemiumIntroductionModalOpenedSubject.asObservable();
  public isCategoriesConsumerMessageOpen$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService,
    private gtmService: GoogleTagManagerService,
    private dashboardStatisticsService: DashboardStatisticsService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem(constants.currentUserStorageKey) || '{}')
    );
    this.currentUser = this.currentUserSubject.asObservable();

    this.currentUserSubject
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((a, b) => a.data?.email === b.data?.email)
      )
      .subscribe((user) => {
        if (!this.isFreemiumIntroductionFinished()) {
          this.setFreemiumIntroductionModalOpened(true);
        }
        this._categoriesEnabled$.next(
          user.data?.groupPermissions?.some(
            (groupPermission) => groupPermission.permission === 'CATEGORIES'
          ) || false
        );
        this._userRoles$.next({
          export: user.data?.roles?.includes(UserRole.Exporter) || false,
        });
        this._incomeGroupEnabled$.next(
          user.data?.groupPermissions?.some(
            (groupPermission) => groupPermission.permission === 'INCOME_BRIDGES'
          ) || false
        );
        this._permissions$.next({
          isFreemium: user.data?.freemium || false,
          isGenerationsAvailable:
            user.data?.groupPermissions?.some(
              (groupPermission) => groupPermission.permission === 'GENERATIONS'
            ) || false,
          freemiumCategory:
            user.data?.groupPermissions?.find(
              (groupPermission) => groupPermission.permission === 'CATEGORIES'
            )?.permissionExtended?.[0] || '',
          categories:
            user.data?.groupPermissions?.find(
              (groupPermission) => groupPermission.permission === 'CATEGORIES'
            )?.permissionExtended || [],
          allowedCountries: user.data?.groupPermissions?.find(
            (groupPermission) => groupPermission.permission === 'COUNTRIES'
          ),
          allowedCountryCities: user.data?.groupPermissions?.find(
            (groupPermission) => groupPermission.permission === 'COUNTRY_CITIES'
          ),
          allowedCities: user.data?.groupPermissions?.find(
            (groupPermission) => groupPermission.permission === 'CITIES'
          ),
          allowedYears:
            user.data?.groupPermissions
              ?.find(
                (groupPermission) => groupPermission.permission === 'YEARS'
              )
              ?.permissionExtended?.map((year) => Number(year)) || [],
        });
        this.gtmService.pushTag({
          event: 'userLogin',
          userEmail: user.data?.email || 'unknown',
        });
        if (user.data?.freemium) {
          localStorage.setItem('isOnboarded', 'true');
        }
      });
  }

  public setFreemiumIntroductionModalOpened(isOpened: boolean): void {
    this.freemiumIntroductionModalOpenedSubject.next(isOpened);
  }

  public isFreemiumIntroductionFinished(): boolean {
    return (
      localStorage.getItem('isFreemiumIntroductionFinished') !== null &&
      localStorage.getItem('isFreemiumIntroductionFinished') === 'true'
    );
  }

  public setFreemiumIntroductionAsSeen() {
    localStorage.setItem('isFreemiumIntroductionFinished', 'true');
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public switchCategoriesConsumerDemand(val: boolean) {
    if (
      this._permissions$.getValue().allowedCities?.permission ||
      this._permissions$.getValue().allowedCountryCities?.permission
    ) {
      return this.isCategoriesConsumerMessageOpen$.next(true);
    }
    this.setCategoriesEnabled();
  }

  public setCategoriesEnabled(val?: boolean) {
    if (val !== undefined) {
      this._categoriesEnabled$.next(val);
      return;
    }
    this._categoriesEnabled$.next(!this._categoriesEnabled$.getValue());
  }

  public login(username: string, password: string): Observable<User> {
    const body = new LoginRequest(username, password);

    return this.http
      .post<AuthResponse>(constants.uriParts.userLogin, body)
      .pipe(
        switchMap((authResponse) => {
          const token: string = authResponse.token;
          if (!token) {
            throw throwError(new Error('Error getting the access token.'));
          }
          this.setUser(token);

          return this.getCurrentUserInfo();
        })
      );
  }

  public impersonate(userId: string): Observable<User> {
    return this.http
      .get<AuthResponse>(constants.uriParts.impersonate + '/' + userId)
      .pipe(
        switchMap((authResponse) => {
          const token: string = authResponse.token;
          if (!token) {
            throw throwError(new Error('Error getting the access token.'));
          }
          this.setUser(token);

          return this.getCurrentUserInfo();
        })
      );
  }

  public hasAccess(roles?: UserRole[], url?: string) {
    if (!this._permissions$.value && url === '/size-of-prize') {
      return false;
    }
    if (roles) {
      return roles.some((userRole) =>
        this.currentUserValue.roles.some(
          (currentUserRole) => userRole === currentUserRole
        )
      );
    }

    return true;
  }

  public displayContinets(): boolean {
    return !this.isLoggedIn() || this.currentUserValue.data?.freemium || false;
  }

  public isLoggedIn(): boolean {
    return !!this.hasToken();
  }

  public hasToken(): boolean {
    return !!(this.currentUserValue && this.currentUserValue.token);
  }

  public logout(): void {
    this.dashboardStatisticsService.cityCountries.clear();
    localStorage.removeItem(constants.currentUserStorageKey);
    this.currentUserSubject.next(new User(''));
    this.cookieService.deleteAll();

    this.router.navigateByUrl(constants.uriParts.login);

    Sentry.setUser(null);
  }

  public getCurrentFullName(): string {
    return AuthService.getUserFullName(this.currentUserValue.data);
  }

  public static getUserFullName(userData?: UserData): string {
    return `${userData?.firstName || ''} ${userData?.lastName || ''}`;
  }

  public updateUserData(userData: UserData): User {
    return this.setUser(this.currentUserValue.token, {
      ...userData,
    });
  }

  public resetPasswordRequest(email: string): Observable<any> {
    return this.http.post<AuthResponse>(
      constants.uriParts.resetPasswordRequest,
      { email }
    );
  }

  public resetPassword(
    email: string,
    emailSecret: string,
    password: string
  ): Observable<any> {
    return this.http.post<AuthResponse>(constants.uriParts.resetPassword, {
      email,
      emailSecret,
      password,
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getCurrentUserInfo(): Observable<User> {
    const isOnboarding =
      window.location.href.indexOf(constants.uriParts.onboarding) !== -1;
    const isResetPasswordScreen =
      window.location.href.indexOf(constants.uriParts.resetPasswordScreen) !==
      -1;

    let requestObservable = this.http.get<UserData>(
      constants.uriParts.loggedUser
    );

    if (isOnboarding || isResetPasswordScreen) {
      const userData = new UserData();
      requestObservable = of(userData);
    }

    return requestObservable.pipe(
      catchError(() => of(this.setUser(''))),
      map((userData: UserData) => {
        if (!userData) {
          return this.setUser('');
        }
        return this.setUser(this.currentUserValue.token, userData);
      })
    );
  }

  private setUser(token: string, userData?: UserData): User {
    const user = new User(token, userData);
    if (!user.data?.email?.endsWith(`@worlddata.io`)) {
      this.hj('identify', user.data?.id, {
        WDProUserId: user.data?.id,
        WDProOrganizationName: user.data?.organizationName,
      });
    }

    Sentry.setUser({
      id: user.data?.id,
      username: `${user.data?.firstName} ${user.data?.lastName}`,
      email: user.data?.email,
    });
    localStorage.setItem(constants.currentUserStorageKey, user.toString());
    this.currentUserSubject.next(user);
    return user;
  }

  public premiumRequest(category: string) {
    this.http
      .get(constants.uriParts.notificationsPremiumRequest, {
        params: {
          categoryName: category,
        },
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();
  }

  public requestCallBack() {
    return this.http
      .get(constants.uriParts.notificationsCallMeBack)
      .pipe(takeUntil(this.ngUnsubscribe));
  }
}
